import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { getGalleryItems, deleteGalleryItem } from '../../services/galleryApi';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Image } from 'antd'; // Keeping Antd's Image for the nice light-box preview feature
import DataTable from '../common/DataTable';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const GalleryList = forwardRef(function GalleryList({ onEdit }, ref) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchGalleryItems = async () => {
    setLoading(true);
    try {
      const data = await getGalleryItems();
      setItems(data);
    } catch (error) {
      toast.error('Failed to fetch gallery items');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: fetchGalleryItems
  }));

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteGalleryItem(deleteId);
      toast.success('Gallery item deleted successfully');
      fetchGalleryItems();
      setDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete gallery item');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      id: 'imageUrl',
      label: 'COVER IMAGE',
      width: 150,
      render: (row) => {
        return row?.coverImage && (
          <Image.PreviewGroup items={[row.coverImage]}>
            <Image
              alt="gallery preview"
              width={80}
              height={60}
              src={row.coverImage}
              style={{ objectFit: 'cover', borderRadius: '6px' }}
            />
          </Image.PreviewGroup>
        );
      },
    },
    { id: 'title', label: 'GALLERY TITLE' },
    { id: 'folderName', label: 'FOLDER NAME' },
    {
      id: 'actions',
      label: 'ACTIONS',
      align: 'right',
      width: 120,
      render: (row) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton 
            onClick={() => onEdit(row)}
            size="small"
            sx={{ 
              color: '#94a3b8', 
              '&:hover': { color: '#2563eb', bgcolor: '#eff6ff' }, 
              transition: 'all 0.2s'
            }}
          >
            <EditIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton 
            onClick={() => setDeleteId(row.id)}
            size="small"
            sx={{ 
              color: '#94a3b8', 
              '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' },
              transition: 'all 0.2s',
              ml: 1
            }}
          >
            <DeleteIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable 
        premium={true}
        columns={columns} 
        data={items} 
        loading={loading} 
        emptyTitle="No Images Found"
        emptySubtitle="There are no gallery items to display yet."
      />
      
      <DeleteConfirmDialog
        open={!!deleteId}
        onClose={() => !deleteLoading && setDeleteId(null)}
        onConfirm={confirmDelete}
        itemName="gallery item"
        loading={deleteLoading}
      />
    </>
  );
});

export default GalleryList;
